import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  CircularProgress,
  Stack,
  Tab,
  Typography,
  IconButton,
  Tooltip,
  TableCell,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { Visibility, FileDownload } from "@mui/icons-material";
import dayjs from "dayjs";
import { GenericTable } from "@/components/GenericTable/GenericTable";
import { ServiceRequestActions } from "@/pages/ehrrecords/components/patientSummaryAccordions/ServiceRequest/ServiceRequestAction";
import { useCallback, useState } from "react";
import { FollowUpModal } from "../../../shared/FollowUpModal";
import { FollowUpActions } from "../FollowUpActions/FollowUpActions";
import { PatchEHRPoster } from "@/services/fhir/patientsummary/PatchEHRPoster";
import { usePostAttendanceFollowUpSendDialog } from "../../../../hooks/usePostAttendanceFollowUpSendDialog";
import { SendWhatsAppMessageDialog } from "src-next/modules/crm-clinical/messaging";
import { toast } from "react-toastify";
import { GetBinary } from "@/services/fhir/patientsummary/Binary/GetBinary";
import { isMobile, isMobileSafari, isAndroid } from "react-device-detect";
import { IframeDialog } from "@/pages/ehrrecords/components/IframeDialog";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import {
  filterEntriesByOrganization,
  getFollowUpOrganizationFilterMessage,
} from "../../utils/postAttendanceHelpers";

interface ExamsProps {
  data: any;
  resultsData: any;
  exames: any;
  loading?: boolean;
  onUpdate: () => void;
  patientData?: { id: string; name: string; phone?: string };
}

export function Exams({
  data,
  resultsData,
  exames,
  loading = false,
  onUpdate,
  patientData,
}: ExamsProps) {
  const { organizationId, organization } = useOrganizationContext();
  const results = resultsData?.[0]?.entry;
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState("pending");
  const [sendingFollowUp, setSendingFollowUp] = useState(false);
  const [openViewer, setOpenViewer] = useState(false);
  const [binaryUrl, setBinaryUrl] = useState("");

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const handleFollowUpSent = useCallback(
    async ({ row }: { row: any }) => {
      const notificationSent = row?.fullResource?.extension?.find(
        (extension: any) => extension?.url.includes("notificationSent"),
      );

      if (!notificationSent && row?.id) {
        await PatchEHRPoster({
          resourceType: "DocumentReference",
          resourceId: row.id,
          operation: "add",
          path: "/extension",
          value: [
            {
              url: "https://fluxmed.com.br/fhir/StructureDefinition/notificationSent",
              valueString: true,
            },
          ],
        });
      }

      onUpdate();
    },
    [onUpdate],
  );

  const { sendFollowUp, sendDialogProps } = usePostAttendanceFollowUpSendDialog({
    onFollowUpSent: handleFollowUpSent,
  });

  const handleOpenModal = (item: any) => {
    setSelectedItem(item);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedItem(null);
  };

  const handleSendFollowUp = async () => {
    if (!selectedItem) return;

    setSendingFollowUp(true);
    try {
      const sent = await sendFollowUp({
        row: selectedItem,
        type: "exam",
        patientData,
        exames,
      });

      if (sent) {
        handleCloseModal();
      }
    } finally {
      setSendingFollowUp(false);
    }
  };

  const documentReference = data?.[0]?.entry?.filter(
    (el: any) =>
      el?.resource?.resourceType === "DocumentReference" &&
      el?.resource?.category?.[0]?.coding?.[0]?.display !== "Procedimento"
  );

  const columns = [
    {
      column: "Descrição",
      rowName: "description",
    },
    { column: "Data", rowName: "date" },
    { column: "Categoria", rowName: "category" },
    { column: "Profissional solicitante", rowName: "practitioner" },
  ];

  function translateCategory(category: any) {
    if (category === "Laboratory procedure") {
      return "Exame Laboratorial";
    } else if (category === "Imaging") {
      return "Exame de imagem";
    } else {
      return category;
    }
  }

  function cleanAndJoin(expressions: any, delimiter = ", ") {
    return Array.from(
      new Set(expressions.filter((item: any) => item && item.trim() !== ""))
    ).join(delimiter);
  }

  const formatExamsData = (documentReference: any[]) => {
    return (
      documentReference
        ?.filter(
          (filterEl: any) =>
            filterEl?.resource?.category?.[0]?.coding?.[0]?.display ===
            "Solicitação"
        )
        .map((el: any) => {
          const auxPractitioner = el?.resource?.contained?.find(
            (contained: any) => contained?.resourceType === "Practitioner"
          );
          const auxSpecialty =
            el?.resource?.practiceSetting?.coding?.[0]?.display;

          const auxDescription = exames?.[0]?.entry?.filter((request: any) => {
            const findRef = el?.resource?.basedOn?.find((basedOn: any) =>
              basedOn?.reference.includes(request.resource.id)
            );
            return findRef;
          });

          const auxCategory = cleanAndJoin(
            auxDescription
              ?.filter(
                (item: any) =>
                  item !== null && item !== undefined && item !== ""
              )
              ?.map(
                (el: any) => el?.resource?.category?.[0]?.coding?.[0].display
              )
          );

          const auxResults = results?.find((result: any) => {
            const findRef = auxDescription?.find(
              (service: any) =>
                service?.resource?.id ===
                result?.resource?.basedOn
                  ?.find((bas: any) => bas.reference.includes("ServiceRequest"))
                  ?.reference?.split("/")?.[1]
            );
            return findRef;
          });

          const resultUrl =
            auxResults?.resource?.content?.[0]?.attachment?.url || "";
          const auxType = el?.resource?.type?.coding?.[0]?.display || "-";

          return {
            id: el?.resource?.id,
            type: auxType,
            encounterId: el?.resource?.context?.[1]?.reference?.split("/")?.[1],
            description:
              auxDescription?.map((serviceRequest: any) => (
                <p key={serviceRequest?.resource?.id}>
                  {
                    serviceRequest?.resource?.code?.concept?.coding?.[0]
                      ?.display
                  }
                </p>
              )) || "",
            category: translateCategory(auxCategory),
            date: dayjs(el?.resource?.date).format("DD/MM/YYYY") || "-",
            specialty: auxSpecialty || "-",
            practitioner:
              auxPractitioner?.name?.[0]?.given?.[0] ||
              auxPractitioner?.identifier?.[0]?.value ||
              "-",
            binaryUrl: el?.resource?.content?.[0]?.attachment?.url || "",
            auxResults,
            resultUrl,
            fullResource: el?.resource,
            serviceRequestId: auxDescription?.[0]?.resource?.id,
            // Requester fica no ServiceRequest vinculado, não no DocumentReference
            requesterReference:
              auxDescription?.[0]?.resource?.requester?.reference,
          };
        })
        ?.sort((a: any, b: any) => {
          const dateA = dayjs(a.date, "DD/MM/YYYY");
          const dateB = dayjs(b.date, "DD/MM/YYYY");
          return dateB.diff(dateA);
        }) || []
    );
  };

  const allExams = formatExamsData(documentReference);
  const pendingExamsWithoutResults = allExams.filter(
    (el: any) => !el?.auxResults,
  );

  // Aba Pendentes: exibe apenas registros da clínica logada (acompanhamento via WhatsApp)
  const pendingExams = filterEntriesByOrganization(
    pendingExamsWithoutResults,
    organizationId,
    (el) => el?.requesterReference,
  );
  const completedExams = allExams.filter((el: any) => el?.auxResults);

  const renderStatusMessage = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              backgroundColor: "info.lighter",
              p: 1.5,
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Typography variant="body1" color="text.secondary">
              {getFollowUpOrganizationFilterMessage(organization?.name)}
            </Typography>
          </Stack>
        );
      case "completed":
        return (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Exames que já foram realizados e possuem resultados disponíveis.
          </Typography>
        );
      default:
        return null;
    }
  };

  const handleOpenViewer = () => {
    setOpenViewer(true);
  };

  const handleCloseViewer = () => {
    setBinaryUrl("");
    setOpenViewer(false);
  };

  const base64ToBlob = (base64: any, contentType = "") => {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  };

  async function downloadBase64File(binary: string) {
    try {
      const response = await GetBinary({
        urlBinary: binary,
      });
      const blob = base64ToBlob(response?.data, response?.contentType);
      const fileUrl = URL.createObjectURL(blob);
      const auxFile = {
        fileUrl,
        fileName: `Documento.pdf`,
      };

      if (isMobileSafari && !isAndroid) {
        if (auxFile) {
          const link = document.createElement("a");
          link.href = auxFile?.fileUrl;
          link.download = auxFile?.fileName;
          link.click();
        } else {
          alert(
            "O arquivo ainda está sendo preparado, por favor tente novamente."
          );
        }
      } else {
        window.open(auxFile.fileUrl, "_blank");
      }
    } catch (err) {
      console.log("err", err);
      toast.error("Erro ao baixar o arquivo");
    }
  }

  const renderResultActions = (row: any) => {
    if (!row?.resultUrl) return null;

    return (
      <TableCell align="center">
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={1}
        >
          <Tooltip title="Baixar resultado">
            <IconButton
              onClick={() => downloadBase64File(row.resultUrl)}
              size="small"
              color="primary"
            >
              <FileDownload />
            </IconButton>
          </Tooltip>
          <Tooltip title="Visualizar resultado">
            <IconButton
              onClick={() => {
                setBinaryUrl(row.resultUrl);
                if (isMobile) {
                  downloadBase64File(row.resultUrl);
                } else {
                  handleOpenViewer();
                }
              }}
              size="small"
              color="primary"
            >
              <Visibility />
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
    );
  };

  return (
    <>
      <Accordion defaultExpanded sx={{ width: "100%" }} elevation={3}>
        <AccordionSummary
          expandIcon={<GridExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Stack direction="row" alignItems="center" gap={1}>
            Exames
            <Avatar
              sx={{
                width: 26,
                height: 26,
                fontSize: "14px",
                fontWeight: 600,
                backgroundColor: "primary100.main",
                color: "primary1000.main",
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                allExams?.length || "0"
              )}
            </Avatar>
          </Stack>
        </AccordionSummary>

        <AccordionDetails>
          <TabContext value={selectedTab}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList onChange={handleChangeTab} variant="scrollable">
                <Tab
                  label={`Pendentes (${pendingExams.length})`}
                  value="pending"
                />
                <Tab
                  label={`Realizados (${completedExams.length})`}
                  value="completed"
                />
              </TabList>
            </Box>

            <TabPanel value="pending" sx={{ p: 2 }}>
              {renderStatusMessage("pending")}
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <GenericTable
                  tableColumns={columns}
                  tableRows={pendingExams}
                  actions={(row: any) => (
                    <FollowUpActions row={row} onOpenModal={handleOpenModal} />
                  )}
                />
              )}
            </TabPanel>

            <TabPanel value="completed" sx={{ p: 2 }}>
              {renderStatusMessage("completed")}
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <GenericTable
                  tableColumns={columns}
                  tableRows={completedExams}
                  actions={renderResultActions}
                />
              )}
            </TabPanel>
          </TabContext>
        </AccordionDetails>
      </Accordion>

      <FollowUpModal
        open={openModal}
        onClose={handleCloseModal}
        title="Acompanhamento de Exame"
        data={selectedItem}
        type="exam"
        onSendFollowUp={handleSendFollowUp}
        loading={sendingFollowUp}
        patientData={patientData}
      />

      {sendDialogProps?.open ? (
        <SendWhatsAppMessageDialog {...sendDialogProps} />
      ) : null}

      {binaryUrl && openViewer && (
        <IframeDialog
          handleClose={handleCloseViewer}
          isOpen={openViewer}
          data={binaryUrl}
        />
      )}
    </>
  );
}
